import {
    AutocompleteApi,
    AutocompleteState,
    BaseItem
} from "@algolia/autocomplete-core";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use } from "react";

import {
    AutocompleteActions,
    AutocompleteRef
} from "@/components/Search/SearchBar/types/searchBar.types";

type SearchBarContextValue = {
    isOpen: boolean;
    items: Record<string, BaseItem[]> | undefined;
    props: {
        root: {
            role: "combobox";
            "aria-expanded": boolean;
            "aria-haspopup":
                | boolean
                | "dialog"
                | "menu"
                | "true"
                | "false"
                | "grid"
                | "listbox"
                | "tree"
                | undefined;
            "aria-controls": string | undefined;
            "aria-labelledby": string;
        };
        panel: {
            onMouseDown: (event: React.MouseEvent) => void;
            onMouseLeave: () => void;
        };
    };
    autocomplete: AutocompleteApi<
        BaseItem,
        React.BaseSyntheticEvent<object, any, any>,
        React.MouseEvent<Element, MouseEvent>,
        React.KeyboardEvent<Element>
    >;
    autocompleteRef: React.RefObject<AutocompleteRef>;
    autocompleteActions: AutocompleteActions | undefined;
    autocompleteState: AutocompleteState<BaseItem> | undefined;
    onKeyDown: (event: React.KeyboardEvent) => void;
    onOpen: () => void;
    onClose: () => void;
};

type SearchBarContextProviderProps = {
    isOpen: boolean;
    items: Record<string, BaseItem[]> | undefined;
    props: {
        root: {
            role: "combobox";
            "aria-expanded": boolean;
            "aria-haspopup":
                | boolean
                | "dialog"
                | "menu"
                | "true"
                | "false"
                | "grid"
                | "listbox"
                | "tree"
                | undefined;
            "aria-controls": string | undefined;
            "aria-labelledby": string;
        };
        panel: {
            onMouseDown: (event: React.MouseEvent) => void;
            onMouseLeave: () => void;
        };
    };
    autocomplete: AutocompleteApi<
        BaseItem,
        React.BaseSyntheticEvent<object, any, any>,
        React.MouseEvent<Element, MouseEvent>,
        React.KeyboardEvent<Element>
    >;
    autocompleteRef: React.RefObject<AutocompleteRef>;
    autocompleteActions: AutocompleteActions | undefined;
    autocompleteState: AutocompleteState<BaseItem> | undefined;
    onKeyDown: (event: React.KeyboardEvent) => void;
    onOpen: () => void;
    onClose: () => void;
    children: React.ReactNode;
};

export const SearchBarContext = createContext<
    SearchBarContextValue | undefined
>(undefined);

export const SearchBarContextProvider = ({
    isOpen,
    items,
    props,
    autocomplete,
    autocompleteRef,
    autocompleteActions,
    autocompleteState,
    onKeyDown,
    onOpen,
    onClose,
    children
}: SearchBarContextProviderProps) => {
    return (
        <SearchBarContext
            value={{
                isOpen: isOpen,
                items: items,
                props: props,
                autocomplete: autocomplete,
                autocompleteRef: autocompleteRef,
                autocompleteActions: autocompleteActions,
                autocompleteState: autocompleteState,
                onKeyDown: onKeyDown,
                onOpen: onOpen,
                onClose: onClose
            }}
        >
            {children}
        </SearchBarContext>
    );
};

export const useSearchBarContext = () => {
    const context = use<SearchBarContextValue | undefined>(SearchBarContext);

    if (!isDefined(context)) {
        throw new Error(`${SearchBarContext.displayName} is undefined.`);
    }

    return context;
};
